import { nanoid } from "nanoid";
import { jsonCopy, compressJson, getJsonFromCompression, getDummyImage } from "../utils/helper";
import { globalGraphClient, getFileUrlFromDriveInfo, getSessionIdFromDriveInfo, getSessionForAccessInfo } from "./GraphMain";
import { checkArInfoTable } from "./graphAppInfoCheckFunctions";
import store from "../stores/StoreRoot";

const chunkMaxSize = 32000;

async function getInfoIdIndex(infoId, fileUrl, sessionId) {
  const tablesUrl = fileUrl + "/workbook/tables/AR_INFO/columns?$top=1";
  let tableInfo = undefined;
  let foundIndex = -1;

  try {
    tableInfo = await globalGraphClient
      .api(tablesUrl)
      .header("workbook-session-id", sessionId)
      .get();
    // get the helper tables
    if (tableInfo?.value?.length > 0) {
      tableInfo.value[0]?.values.forEach((entry, index) => {
        if (entry[0] === infoId) { 
          foundIndex = index;
        }
      })
    }
  } catch (err) {
    console.log("ERROR getTableInfo", err);
  }
  return foundIndex;
}

async function getInfoValue(infoId, fileUrl, sessionId) {
  const tablesUrl = fileUrl + "/workbook/tables/AR_INFO/columns?$top=7";
  let tableInfo = undefined;
  let foundValue = undefined;

  try {
    tableInfo = await globalGraphClient
      .api(tablesUrl)
      .header("workbook-session-id", sessionId)
      .get();
    // get the helper tables
    if (tableInfo?.value?.length > 0) {
      tableInfo.value[0]?.values.forEach((entry, index) => {
        if (entry[0] === infoId) {
          let infoValue = tableInfo.value[1]?.values[index][0] ?? "";
          let infoValue2 = tableInfo.value[2]?.values[index][0] ?? "";
          let infoValue3 = tableInfo.value[3]?.values[index][0] ?? "";
          let infoValue4 = tableInfo.value[4]?.values[index][0] ?? "";
          let infoValue5 = tableInfo.value[5]?.values[index][0] ?? "";
          let infoValue6 = tableInfo.value[6]?.values[index][0] ?? "";
          foundValue = infoValue + infoValue2 + infoValue3 + infoValue4 + infoValue5+ infoValue6;
        }
      })
    }
  } catch (err) {
    console.log("ERROR getTableInfo", err);
  }
  return foundValue;
}

async function writeValueToAddress(fileUrl, sessionId, address, infoValue) {
  let result = false;
  const tablesUrl = fileUrl + "/workbook/worksheets/AR_INFO/range"+address;
  try {
    const workbookRange = {
      "values" : [[infoValue]]
    };
    let res = await globalGraphClient
      .api(tablesUrl)
      .header("workbook-session-id", sessionId)
      .update(workbookRange);
    result = (res?.cellcount === 1)
  } catch (err) {
    console.log("ERROR writeInfoValue", err);
  }
  return result;  
}

function getChunksFromString(str, size) {
  const numChunks = Math.ceil(str.length / size)
  const chunks = new Array(numChunks)

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str.substr(o, size)
  }

  return chunks
}

async function updateInfoValue(fileUrl, sessionId, infoIndex, infoValue) {
  let result = false;
  if (infoIndex < 0) {
    return result;
  }
  let row = String(infoIndex + 1);
  let chunks = getChunksFromString(infoValue, chunkMaxSize);
  let chunksLength = chunks.length;
  if (chunksLength < 6) {
    for (let i = chunksLength; i < 6; ++i) {
      chunks[i] = ""
    }  
  }
  
  chunks.forEach(async (item, index) => {
    let column;
    if (index === 0) column = "B";
    if (index === 1) column = "C";
    if (index === 2) column = "D";
    if (index === 3) column = "E";
    if (index === 4) column = "F";
    if (index === 5) column = "G";

    let address = "(address='"+column+row+"')";
    result = await writeValueToAddress(fileUrl, sessionId, address, item ?? "");
  })
  // also overwrite the other columns - wrong info from last time

  return result;
}

async function newInfoValue(fileUrl, sessionId, infoId, infoValue) {
  let result = false;

  try {
    console.log("INFO new InfoRecord - add", infoId);
    let chunks = getChunksFromString(infoValue, chunkMaxSize);

    const infoRows = {
      index: null,
      values: [[infoId, 
        chunks[0] ?? "", 
        chunks[1] ?? "", 
        chunks[2] ?? "", 
        chunks[3] ?? "", 
        chunks[4] ?? "", 
        chunks[5] ?? ""
      ]]
    };      
    await globalGraphClient.api(fileUrl + "/workbook/tables/AR_INFO/rows/add")
      .header("workbook-session-id", sessionId)
      .post(infoRows);
    result = true;
  } catch (err) {
    console.log("ERROR newInfoValue", err);
  }
  return result;
}

export async function duplicateGraphAppInfoValue(driveItemInfo, infoId, prefix = "view") {
  if (!driveItemInfo) return false;

  const fileUrl = getFileUrlFromDriveInfo(driveItemInfo);
  const sessionId = await getSessionIdFromDriveInfo(driveItemInfo);
  let newInfoId;
  let newInfoJson;

  try {
    const res = await getInfoValue(infoId, fileUrl, sessionId);
    let infoValue = getJsonFromCompression(res);
    // assign a new image
    if (infoValue.hasOwnProperty("topImageUrl")) infoValue.topImageUrl = getDummyImage();
    newInfoId = prefix + "_" + nanoid();
    if (prefix === "calculation") {
      infoValue.caption = infoValue.caption + " (copy)";
    };
    if (prefix === "collection") {
      infoValue.name = infoValue.name + " (copy)";
      infoValue.id = newInfoId;
    };
    newInfoJson = jsonCopy(infoValue);

    // compress the configuration
    const version = store.isSafari ? "v2" : null;
    const stateString = compressJson(newInfoJson, version); //"v2"

    // write this view
    await newInfoValue(fileUrl, sessionId, newInfoId, stateString)
  } catch (error) {
    console.log("ERROR duplicateGraphAppInfoValue", error);
  }

  return {
    id: newInfoId,
    data: newInfoJson
  }
}

export async function deleteGraphAppInfoValue(driveItemInfo, infoId) {
  if (!driveItemInfo) return false;

  const fileUrl = getFileUrlFromDriveInfo(driveItemInfo);
  const sessionId = await getSessionIdFromDriveInfo(driveItemInfo);
  // check already existing description value
  const infoIndex = await getInfoIdIndex(infoId, fileUrl, sessionId);
  // is existing - update description value
  if (infoIndex > -1) {
    const rowIndex = String(infoIndex-1);
    try {
      await globalGraphClient
        .api(fileUrl+'/workbook/tables/AR_INFO/rows/$/ItemAt(index='+String(rowIndex)+')')
        .header("workbook-session-id", sessionId)
        .delete();
      return true;
    } catch (err) {
      console.log("ERROR deleteGraphAppInfoValue", err);
      return false;
    }
  }
}

export async function writeGraphAppInfoValue(accessInfo, infoId, infoValue, messageCallback) {
  // let compressed = compressJson(infoValue);
  
  const {client, sessionId, fileUrl} = await getSessionForAccessInfo(accessInfo);
  let result = false;
  let infoTableFound = await checkArInfoTable(fileUrl, sessionId, client, true);
  // check ar-tables
  if (!infoTableFound) {
    // create the system tables
    console.log("INFO - info system table not found - need to create system tables");
    return false;
  }
  // check already existing description value
  const infoIndex = await getInfoIdIndex(infoId, fileUrl, sessionId);
  // is existing - update description value
  if (infoIndex > -1) {
    result = await updateInfoValue(fileUrl, sessionId, infoIndex, infoValue);
  }
  // not existing - add a new row to the table
  if (infoIndex === -1) {
    result = await newInfoValue(fileUrl, sessionId, infoId, infoValue);
  }
  return result;
}

